import { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Badge, Alert, Table } from 'react-bootstrap';
import { FaArrowLeft, FaGift, FaCheckCircle, FaTimesCircle, FaCalendar, FaTrophy, FaToggleOn, FaToggleOff, FaFilter } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import StandardModal from '../../components/admin/StandardModal';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './InscripcionManagement.css';

const PromosManagement = () => {
  const navigate = useNavigate();

  // Estados
  const [referidos, setReferidos] = useState([]);
  const [promos, setPromos] = useState([]);
  const [promoSeleccionada, setPromoSeleccionada] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [loading, setLoading] = useState(true);
  const [loadingPromos, setLoadingPromos] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showValidarModal, setShowValidarModal] = useState(false);
  const [referidoSeleccionado, setReferidoSeleccionado] = useState(null);
  const [torneoSeleccionado, setTorneoSeleccionado] = useState(null);

  // Cargar promos al montar
  useEffect(() => {
    cargarPromos();
  }, []);

  // Recargar referidos cuando cambia filtro o promo
  const cargarReferidos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { estado: filtroEstado };
      if (promoSeleccionada) params.promo_id = promoSeleccionada;

      const response = await api.get('/postgres/referidos/admin/pendientes', { params });

      if (response.data.success) {
        setReferidos(response.data.data);
      } else {
        toast.error('Error al cargar referidos');
      }
    } catch (error) {
      console.error('Error cargando referidos:', error);
      toast.error('Error al cargar referidos');
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, promoSeleccionada]);

  useEffect(() => {
    cargarReferidos();
  }, [cargarReferidos]);

  const cargarPromos = async () => {
    setLoadingPromos(true);
    try {
      const response = await api.get('/postgres/promos');
      if (response.data.success) {
        setPromos(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando promos:', error);
    } finally {
      setLoadingPromos(false);
    }
  };

  const handleTogglePromo = async (promo) => {
    try {
      const response = await api.put(`/postgres/promos/${promo.id}/toggle`);
      if (response.data.success) {
        toast.success(response.data.message);
        cargarPromos();
      }
    } catch (error) {
      console.error('Error toggling promo:', error);
      toast.error('Error al cambiar estado de la promoción');
    }
  };

  const handleValidarClick = (referido) => {
    setReferidoSeleccionado(referido);
    if (referido.inscripciones_amigo && referido.inscripciones_amigo.length > 0) {
      setTorneoSeleccionado(referido.inscripciones_amigo[0].torneo_id);
    }
    setShowValidarModal(true);
  };

  const handleValidar = async () => {
    if (!torneoSeleccionado) {
      toast.error('Debes seleccionar un torneo');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post(`/postgres/referidos/${referidoSeleccionado.id}/validar`, {
        torneo_id: torneoSeleccionado,
        notas: `Validado desde panel admin. Jornada añadida al torneo ${torneoSeleccionado}`
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowValidarModal(false);
        setReferidoSeleccionado(null);
        setTorneoSeleccionado(null);
        cargarReferidos();
      } else {
        toast.error(response.data.message || 'Error al validar referido');
      }
    } catch (error) {
      console.error('Error validando referido:', error);
      toast.error(error.response?.data?.message || 'Error al validar referido');
    } finally {
      setProcessing(false);
    }
  };

  const handleNoValidar = async (referido) => {
    if (!window.confirm(`¿Seguro que quieres rechazar el referido de ${referido.comprador_nombre}?`)) return;

    try {
      const response = await api.post(`/postgres/referidos/${referido.id}/no-validar`, {
        notas: 'No validado desde panel admin'
      });

      if (response.data.success) {
        toast.success(response.data.message);
        cargarReferidos();
      } else {
        toast.error(response.data.message || 'Error al rechazar referido');
      }
    } catch (error) {
      console.error('Error rechazando referido:', error);
      toast.error(error.response?.data?.message || 'Error al rechazar referido');
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'pendiente': return <span className="text-warning fw-bold">Pendiente</span>;
      case 'validado': return <span className="text-success fw-bold">Validado</span>;
      case 'no_validado': return <span className="text-danger fw-bold">No Validado</span>;
      default: return <span className="text-muted">{estado}</span>;
    }
  };

  return (
    <AdminLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 gap-sm-3 w-100 w-lg-auto">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/admin/matches')}
                  size="sm"
                  className="d-flex align-items-center"
                >
                  <FaArrowLeft className="me-2" />
                  <span className="d-none d-sm-inline">Volver</span>
                </Button>
                <div>
                  <h2 className="mb-0 fs-4 fs-lg-2">
                    <FaGift className="me-2" />
                    <span className="d-none d-sm-inline">Gestión de Promociones</span>
                    <span className="d-sm-none">Promos</span>
                  </h2>
                  <p className="text-muted mb-0 small">
                    Validar referidos y añadir jornadas gratis
                  </p>
                </div>
              </div>
              <Button
                variant="outline-primary"
                onClick={cargarReferidos}
                disabled={loading}
                size="sm"
                className="w-100 w-sm-auto"
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Actualizar'}
              </Button>
            </div>
          </Col>
        </Row>

        {/* Panel de Promociones */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header className="bg-dark text-white">
                <h5 className="mb-0"><FaGift className="me-2" />Promociones</h5>
              </Card.Header>
              <Card.Body>
                {loadingPromos ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : promos.length === 0 ? (
                  <Alert variant="info" className="mb-0">No hay promociones creadas</Alert>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <Table hover size="sm" className="mb-0" style={{ minWidth: '600px' }}>
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Tipo</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promos.map((promo) => (
                          <tr key={promo.id}>
                            <td className="fw-bold">{promo.nombre}</td>
                            <td><Badge bg="info">{promo.tipo}</Badge></td>
                            <td>
                              {promo.activa ? (
                                <Badge bg="success">Activa</Badge>
                              ) : (
                                <Badge bg="secondary">Inactiva</Badge>
                              )}
                            </td>
                            <td>
                              <Button
                                variant={promo.activa ? 'outline-danger' : 'outline-success'}
                                size="sm"
                                onClick={() => handleTogglePromo(promo)}
                              >
                                {promo.activa ? (
                                  <><FaToggleOff className="me-1" />Desactivar</>
                                ) : (
                                  <><FaToggleOn className="me-1" />Activar</>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros en línea */}
        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
          <Form.Group>
            <Form.Label className="small fw-bold mb-1">
              <FaFilter className="me-1" />Estado
            </Form.Label>
            <Form.Select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              size="sm"
              style={{ minWidth: '160px' }}
            >
              <option value="pendiente">Pendientes</option>
              <option value="validado">Validados</option>
              <option value="no_validado">No Validados</option>
              <option value="todos">Todos</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label className="small fw-bold mb-1">
              <FaGift className="me-1" />Promoción
            </Form.Label>
            <Form.Select
              value={promoSeleccionada}
              onChange={(e) => setPromoSeleccionada(e.target.value)}
              size="sm"
              style={{ minWidth: '180px' }}
            >
              <option value="">Todas las promos</option>
              {promos.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        {/* Tabla de Referidos */}
        <Row>
          <Col>
            <Card>
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  Referidos
                  {!loading && (
                    <Badge bg="light" text="dark" className="ms-2">
                      {referidos.length}
                    </Badge>
                  )}
                </h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Cargando referidos...</p>
                  </div>
                ) : referidos.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <FaCheckCircle className="me-2" />
                    No hay referidos con el filtro seleccionado
                  </Alert>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <Table hover style={{ minWidth: '900px' }}>
                      <thead>
                        <tr>
                          <th>Comprador</th>
                          <th>Amigo Referido</th>
                          <th>Fecha</th>
                          <th>Promoción</th>
                          <th>Estado</th>
                          <th>Inscripciones Activas</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referidos.map((referido) => (
                          <tr key={referido.id}>
                            <td>
                              <div>
                                <div className="fw-bold">{referido.comprador_nombre}</div>
                                <small className="text-muted">ID: {referido.futbolista_comprador_id}</small>
                              </div>
                            </td>

                            <td>
                              <div>
                                <div className="fw-bold">{referido.amigo_nombre}</div>
                                <small className="text-muted">ID: {referido.futbolista_amigo_id}</small>
                              </div>
                            </td>

                            <td style={{ whiteSpace: 'nowrap' }}>
                              <FaCalendar className="me-1 text-muted" />
                              {new Date(referido.fecha_referido).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </td>

                            <td>
                              {referido.promo_nombre ? (
                                <div>
                                  <div className="fw-bold">{referido.promo_nombre}</div>
                                  <small className="text-muted">
                                    {referido.promo_jornadas_gratis} jornada(s) gratis
                                  </small>
                                </div>
                              ) : (
                                <Badge bg="secondary">Sin promo</Badge>
                              )}
                            </td>

                            <td>{getEstadoTexto(referido.estado)}</td>

                            <td>
                              {referido.inscripciones_amigo && referido.inscripciones_amigo.length > 0 ? (
                                <div>
                                  {referido.inscripciones_amigo.map((insc, idx) => (
                                    <div key={idx} className="mb-1">
                                      <FaTrophy className="me-1 text-warning" />
                                      <small>
                                        {insc.torneo_nombre}
                                        <Badge bg="info" className="ms-2">
                                          {insc.saldo_jornadas} jornadas
                                        </Badge>
                                      </small>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <Badge bg="warning">Sin inscripciones activas</Badge>
                              )}
                            </td>

                            <td style={{ whiteSpace: 'nowrap' }}>
                              {referido.estado === 'pendiente' && (
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleValidarClick(referido)}
                                    disabled={!referido.inscripciones_amigo || referido.inscripciones_amigo.length === 0}
                                  >
                                    <FaCheckCircle className="me-1" />
                                    Validar
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleNoValidar(referido)}
                                  >
                                    <FaTimesCircle className="me-1" />
                                    No Validar
                                  </Button>
                                </div>
                              )}
                              {referido.estado !== 'pendiente' && referido.notas && (
                                <small className="text-muted">{referido.notas}</small>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de Validación */}
        <StandardModal
          isOpen={showValidarModal}
          onClose={() => setShowValidarModal(false)}
          title="Validar Referido"
          size="medium"
        >
          {referidoSeleccionado && (
            <div style={{
              padding: window.innerWidth <= 768 ? '10px' : '20px 0',
              height: window.innerWidth <= 768 ? '100%' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: window.innerWidth <= 768 ? 'auto' : 'visible'
            }}>
              <div style={{
                marginBottom: window.innerWidth <= 768 ? '20px' : '30px',
                flex: window.innerWidth <= 768 ? '1' : 'none',
                overflowY: window.innerWidth <= 768 ? 'auto' : 'visible'
              }}>
                <p style={{
                  marginBottom: '15px',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                  color: '#2c3e50'
                }}>
                  <strong>¿Confirmas que deseas validar este referido?</strong>
                </p>

                <div style={{
                  background: '#f8f9fa',
                  padding: window.innerWidth <= 768 ? '12px' : '15px',
                  borderRadius: '8px',
                  margin: '15px 0'
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '5px'
                    }}>
                      Comprador
                    </span>
                    <span style={{
                      fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                      color: '#2c3e50',
                      fontWeight: '500',
                      wordBreak: 'break-word'
                    }}>
                      {referidoSeleccionado.comprador_nombre}
                    </span>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '5px'
                    }}>
                      Amigo Referido
                    </span>
                    <span style={{
                      fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                      color: '#2c3e50',
                      fontWeight: '500',
                      wordBreak: 'break-word'
                    }}>
                      {referidoSeleccionado.amigo_nombre}
                    </span>
                  </div>

                  <div style={{ marginBottom: '0' }}>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '5px'
                    }}>
                      Jornadas a añadir
                    </span>
                    <span style={{
                      fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                      color: '#2c3e50',
                      fontWeight: '500'
                    }}>
                      {referidoSeleccionado.promo_jornadas_gratis || 1} jornada(s) gratis
                    </span>
                  </div>
                </div>

                <Form.Group style={{ marginBottom: '15px' }}>
                  <Form.Label style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Selecciona el torneo:
                  </Form.Label>
                  <Form.Select
                    value={torneoSeleccionado || ''}
                    onChange={(e) => setTorneoSeleccionado(parseInt(e.target.value))}
                    style={{
                      fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                      padding: window.innerWidth <= 768 ? '8px' : '10px'
                    }}
                  >
                    <option value="">-- Selecciona un torneo --</option>
                    {referidoSeleccionado.inscripciones_amigo?.map((insc) => (
                      <option key={insc.torneo_id} value={insc.torneo_id}>
                        {insc.torneo_nombre} (Saldo: {insc.saldo_jornadas})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  background: '#d1ecf1',
                  padding: window.innerWidth <= 768 ? '12px' : '15px',
                  borderRadius: '8px',
                  borderLeft: '4px solid #0dcaf0',
                  margin: '15px 0'
                }}>
                  <span className="material-icons" style={{
                    color: '#0c5460',
                    fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                    flexShrink: 0
                  }}>info</span>
                  <p style={{
                    margin: 0,
                    fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                    color: '#0c5460',
                    lineHeight: '1.4'
                  }}>
                    Al validar se añadirá {referidoSeleccionado.promo_jornadas_gratis || 1} jornada(s) gratis y se inscribirá en la siguiente jornada disponible.
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                gap: '10px',
                justifyContent: 'flex-end',
                paddingTop: window.innerWidth <= 768 ? '15px' : '20px',
                borderTop: '1px solid #dee2e6',
                flexShrink: 0
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowValidarModal(false)}
                  disabled={processing}
                  style={{
                    minWidth: window.innerWidth <= 768 ? '100%' : '120px',
                    padding: window.innerWidth <= 768 ? '12px' : '8px 16px'
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleValidar}
                  disabled={processing || !torneoSeleccionado}
                  style={{
                    minWidth: window.innerWidth <= 768 ? '100%' : '120px',
                    padding: window.innerWidth <= 768 ? '12px' : '8px 16px'
                  }}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Validando...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" />
                      {window.innerWidth <= 768 ? 'Validar' : 'Validar Referido'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </StandardModal>
      </Container>
    </AdminLayout>
  );
};

export default PromosManagement;

